// SPDX-License-Identifier: BUSL-1.1

/**
 * Maps audit entries to natural-language display parts for the ActivityFeed.
 *
 */

import type { AuditEntry } from '@/types/api';

export interface FormattedAuditAction {
  /** Verb phrase, e.g. 'promoted', 'drafted', 'toggled'. */
  verb: string;
  /** Human-readable resource label, e.g. 'sdui:score-delta'. */
  resource: string;
  /** Color variant for the ResourcePill. */
  variant: 'blue' | 'green' | 'amber' | 'red' | 'purple';
  /** Optional trailing text, e.g. 'to production'. */
  suffix?: string;
}

/**
 * Parses entry.action (e.g. 'config.promote') + entry.resourceType/resourceId
 * into a display-ready shape. Pure — no side effects, no hooks.
 */
export function formatAuditAction(entry: AuditEntry): FormattedAuditAction {
  const actionSuffix = entry.action.split('.').pop() ?? entry.action;
  const resource = `${entry.resourceType}:${entry.resourceId}`;

  switch (actionSuffix) {
    // Config lifecycle
    case 'create':
      return { verb: 'drafted', resource, variant: 'amber' };
    case 'stage':
      return { verb: 'staged', resource, variant: 'blue' };
    case 'promote':
      return { verb: 'promoted', resource, variant: 'green', suffix: 'to production' };
    case 'rollback':
      return { verb: 'rolled back', resource, variant: 'red' };

    // Flags
    case 'toggle':
      return { verb: 'toggled', resource, variant: 'blue' };
    case 'kill_switch':
      return { verb: 'killed', resource, variant: 'red' };

    // Cache
    case 'invalidate':
      return { verb: 'invalidated', resource, variant: 'blue' };

    // Sessions / agents
    case 'session_start':
      return { verb: 'started', resource, variant: 'green' };
    case 'session_end':
      return { verb: 'ended', resource, variant: 'purple' };
    case 'budget_exceeded':
      return { verb: 'budget exceeded on', resource, variant: 'red' };

    // Fallback: show raw action suffix
    default:
      return { verb: actionSuffix, resource, variant: 'blue' };
  }
}
